import { motion, useScroll, useTransform } from "framer-motion";
import Layout from "../../components/shared/layout/layout";
import { useRef } from "react";

const CorporateTraining = () => {
  const heroRef = useRef(null);

  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  });

  const heroY = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const heroOpacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  const capabilities = [
    {
      number: "01",
      title: "Software Engineering",
      description:
        "Build engineering teams capable of designing, developing, testing and shipping production-grade software.",
      tag: "ENGINEERING",
    },
    {
      number: "02",
      title: "Cybersecurity",
      description:
        "Develop security-first thinking across infrastructure, applications, networks and organizational workflows.",
      tag: "SECURITY",
    },
    {
      number: "03",
      title: "Artificial Intelligence",
      description:
        "Move beyond AI awareness. Embed practical AI systems into real business processes and decision-making.",
      tag: "AI",
    },
    {
      number: "04",
      title: "Data & Systems Thinking",
      description:
        "Help teams understand data, dependencies, architecture and the systems that power modern organizations.",
      tag: "DATA",
    },
    {
      number: "05",
      title: "Product Management",
      description:
        "Turn ideas into measurable products through customer discovery, product strategy and disciplined execution.",
      tag: "PRODUCT",
    },
    {
      number: "06",
      title: "Project Management",
      description:
        "Create execution systems that move initiatives from planning to measurable delivery.",
      tag: "DELIVERY",
    },
    {
      number: "07",
      title: "Team Dynamics",
      description:
        "Build collaborative engineering cultures where communication, ownership and accountability become habits.",
      tag: "PEOPLE",
    },
    {
      number: "08",
      title: "Custom Transformation",
      description:
        "A bespoke program designed around your organization's technology, people and operational challenges.",
      tag: "CUSTOM",
    },
  ];

  const principles = [
    {
      number: "01",
      title: "Think",
      description:
        "Develop the mental models required to solve complex problems.",
    },
    {
      number: "02",
      title: "Build",
      description:
        "Translate knowledge into working systems, products and solutions.",
    },
    {
      number: "03",
      title: "Ship",
      description:
        "Operate with deadlines, constraints, feedback and accountability.",
    },
    {
      number: "04",
      title: "Improve",
      description:
        "Measure outcomes, learn from failure and continuously raise the standard.",
    },
  ];

  return (
    <Layout>
      <main className="overflow-hidden bg-white text-[#07151c]">

        {/* =========================================================
            HERO
        ========================================================= */}
        <section
          ref={heroRef}
          className="relative min-h-[90vh] flex items-center overflow-hidden"
        >
          {/* Ambient background */}
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute -top-40 -right-40 w-[600px] h-[600px] rounded-full bg-[#59b5cd]/20 blur-[130px]" />
            <div className="absolute bottom-[-250px] left-[-150px] w-[600px] h-[600px] rounded-full bg-[#ecb32c]/20 blur-[140px]" />
            <div className="absolute top-[30%] left-[45%] w-[300px] h-[300px] rounded-full bg-[#cf375d]/10 blur-[120px]" />
          </div>

          {/* Grid */}
          <div
            className="absolute inset-0 opacity-[0.035]"
            style={{
              backgroundImage:
                "linear-gradient(#07151c 1px, transparent 1px), linear-gradient(90deg, #07151c 1px, transparent 1px)",
              backgroundSize: "70px 70px",
            }}
          />

          <motion.div
            style={{ y: heroY, opacity: heroOpacity }}
            className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 w-full py-24"
          >
            {/* Eyebrow */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="flex items-center gap-4 mb-10"
            >
              <span className="w-12 h-[2px] bg-[#59b5cd]" />

              <span className="text-xs font-bold tracking-[0.35em] uppercase text-gray-500">
                Zulfah Corporate Transformation
              </span>
            </motion.div>

            {/* Headline */}
            <motion.h1
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.1 }}
              className="max-w-6xl text-[clamp(3rem,8vw,8rem)] font-black leading-[0.92] tracking-[-0.06em]"
            >
              We don't train
              <br />

              <span className="text-gray-300">teams.</span>

              <br />

              We redesign
              <br />

              <span className="bg-gradient-to-r from-[#59b5cd] via-[#3b82f6] to-[#cf375d] bg-clip-text text-transparent">
                performance.
              </span>
            </motion.h1>

            {/* Bottom content */}
            <div className="mt-12 grid lg:grid-cols-[1fr_auto] gap-10 items-end">

              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.35 }}
                className="max-w-2xl"
              >
                <p className="text-lg sm:text-xl text-gray-600 leading-relaxed">
                  Technology changes fast. Your people need more than
                  knowledge. They need the ability to{" "}
                  <span className="text-[#07151c] font-semibold">
                    think, build, ship and adapt.
                  </span>
                </p>

                <div className="mt-8 flex flex-col sm:flex-row gap-4">
                  <a
                    href="https://wa.me/2348084259372"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group inline-flex items-center justify-center gap-3 px-7 py-4 rounded-full bg-[#07151c] text-white font-semibold hover:bg-[#59b5cd] transition-all duration-300"
                  >
                    Design Your Transformation

                    <span className="group-hover:translate-x-1 transition-transform">
                      →
                    </span>
                  </a>

                  <a
                    href="#programs"
                    className="inline-flex items-center justify-center px-7 py-4 rounded-full border border-gray-200 font-semibold hover:border-[#59b5cd] hover:bg-gray-50 transition"
                  >
                    Explore Capabilities
                  </a>
                </div>
              </motion.div>

              {/* Hero stat */}
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.8, delay: 0.5 }}
                className="hidden md:block"
              >
                <div className="relative w-48 h-48 rounded-full border border-gray-200 flex items-center justify-center">
                  <div className="absolute inset-3 rounded-full border border-dashed border-[#59b5cd]/50 animate-spin-slow" />

                  <div className="text-center">
                    <div className="text-5xl font-black text-[#07151c]">
                      500+
                    </div>

                    <div className="text-xs uppercase tracking-widest text-gray-400 mt-2">
                      Professionals
                      <br />
                      Trained
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          </motion.div>

          {/* Scroll indicator */}
          <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-gray-400">
            <span className="text-[9px] tracking-[0.3em] uppercase">
              Scroll
            </span>

            <motion.div
              animate={{ height: [20, 40, 20] }}
              transition={{ duration: 1.5, repeat: Infinity }}
              className="w-[1px] bg-[#59b5cd]"
            />
          </div>
        </section>

        {/* =========================================================
            BIG STATEMENT
        ========================================================= */}
        <section className="relative py-28 sm:py-40 bg-[#07151c] text-white">

          <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-[#59b5cd]/10 blur-[150px] rounded-full" />

          <div className="relative max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">

            <div className="grid lg:grid-cols-[0.7fr_1.3fr] gap-16">

              <div>
                <p className="text-[#59b5cd] text-xs font-bold tracking-[0.3em] uppercase">
                  The problem
                </p>

                <div className="mt-8 text-gray-500 text-sm leading-relaxed max-w-xs">
                  Most corporate training programs optimize for completion.
                  We optimize for what happens after the training ends.
                </div>
              </div>

              <div>
                <h2 className="text-4xl sm:text-6xl lg:text-7xl font-black leading-[0.95] tracking-tight">
                  Knowledge is not the
                  <span className="text-gray-500"> outcome.</span>

                  <br />

                  <span className="text-[#59b5cd]">
                    Performance is.
                  </span>
                </h2>

                <p className="mt-10 text-lg text-white/60 max-w-2xl leading-relaxed">
                  We design learning environments that force people to apply
                  what they know. Through simulations, projects, feedback
                  loops and real-world scenarios, learning becomes behavior.
                  Behavior becomes capability.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* =========================================================
            OLD VS ZULFAH
        ========================================================= */}
        <section className="py-24 sm:py-32 bg-[#f7f9fa]">

          <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">

            <div className="text-center max-w-3xl mx-auto">
              <p className="text-[#cf375d] text-xs font-bold tracking-[0.3em] uppercase">
                A different model
              </p>

              <h2 className="mt-5 text-4xl sm:text-6xl font-black tracking-tight">
                From training
                <span className="text-gray-300"> → </span>
                transformation.
              </h2>
            </div>

            <div className="mt-20 grid lg:grid-cols-2 gap-6">

              {/* Old */}
              <motion.div
                whileHover={{ y: -5 }}
                className="p-8 sm:p-12 rounded-3xl bg-white border border-gray-200"
              >
                <span className="text-xs tracking-[0.25em] text-gray-400 font-bold">
                  THE OLD MODEL
                </span>

                <h3 className="text-3xl sm:text-4xl font-black mt-8">
                  Training
                </h3>

                <div className="mt-10 space-y-5">
                  {[
                    "Slides & presentations",
                    "Passive learning",
                    "Certificates",
                    "Short-term knowledge",
                    "Little accountability",
                  ].map((item) => (
                    <div
                      key={item}
                      className="flex items-center gap-4 text-gray-500"
                    >
                      <span className="text-gray-300">×</span>
                      {item}
                    </div>
                  ))}
                </div>
              </motion.div>

              {/* Zulfah */}
              <motion.div
                whileHover={{ y: -5 }}
                className="relative p-8 sm:p-12 rounded-3xl bg-[#07151c] text-white overflow-hidden"
              >
                <div className="absolute -right-20 -top-20 w-60 h-60 rounded-full bg-[#59b5cd]/20 blur-3xl" />

                <span className="relative text-xs tracking-[0.25em] text-[#59b5cd] font-bold">
                  THE ZULFAH MODEL
                </span>

                <h3 className="relative text-3xl sm:text-4xl font-black mt-8">
                  Transformation
                </h3>

                <div className="relative mt-10 space-y-5">
                  {[
                    "Real-world simulations",
                    "Hands-on execution",
                    "Measurable output",
                    "Continuous feedback",
                    "Behavioral change",
                  ].map((item) => (
                    <div
                      key={item}
                      className="flex items-center gap-4 text-white/80"
                    >
                      <span className="text-[#59b5cd]">✓</span>
                      {item}
                    </div>
                  ))}
                </div>
              </motion.div>

            </div>
          </div>
        </section>

        {/* =========================================================
            FRAMEWORK
        ========================================================= */}
        <section className="py-28 sm:py-40 bg-white">

          <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">

            <div className="max-w-3xl">
              <p className="text-[#59b5cd] text-xs tracking-[0.3em] uppercase font-bold">
                Our methodology
              </p>

              <h2 className="mt-5 text-4xl sm:text-6xl font-black tracking-tight">
                The Zulfah
                <span className="text-gray-300"> execution loop.</span>
              </h2>

              <p className="mt-6 text-gray-500 text-lg leading-relaxed">
                A simple framework designed to turn technical knowledge into
                repeatable professional capability.
              </p>
            </div>

            <div className="mt-20 grid md:grid-cols-2 lg:grid-cols-4 gap-px bg-gray-200 border border-gray-200 rounded-3xl overflow-hidden">

              {principles.map((item, index) => (
                <motion.div
                  key={item.number}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="group bg-white p-8 sm:p-10 min-h-[280px] hover:bg-[#07151c] hover:text-white transition-all duration-500"
                >
                  <span className="text-xs text-gray-400 group-hover:text-[#59b5cd]">
                    {item.number}
                  </span>

                  <h3 className="text-3xl font-black mt-14">
                    {item.title}
                  </h3>

                  <p className="text-gray-500 group-hover:text-white/60 mt-4 text-sm leading-relaxed">
                    {item.description}
                  </p>
                </motion.div>
              ))}

            </div>
          </div>
        </section>

        {/* =========================================================
            CAPABILITIES
        ========================================================= */}
        <section
          id="programs"
          className="py-28 sm:py-40 bg-[#f7f9fa]"
        >

          <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">

            <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-8">

              <div>
                <p className="text-[#cf375d] text-xs tracking-[0.3em] uppercase font-bold">
                  Capability architecture
                </p>

                <h2 className="mt-5 text-4xl sm:text-6xl font-black tracking-tight">
                  What we
                  <span className="text-gray-300"> transform.</span>
                </h2>
              </div>

              <p className="max-w-md text-gray-500 leading-relaxed">
                Every organization has different challenges. We combine
                technical capability, human behavior and execution systems
                around the outcomes that matter to your business.
              </p>

            </div>

            <div className="mt-20 border-t border-gray-200">

              {capabilities.map((item, index) => (
                <motion.div
                  key={item.number}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.05 }}
                  className="group border-b border-gray-200 py-8 sm:py-10 grid grid-cols-[50px_1fr] lg:grid-cols-[70px_1fr_1fr_100px] gap-6 items-center hover:bg-white px-3 sm:px-6 transition-all duration-300"
                >

                  <span className="text-xs text-gray-400">
                    {item.number}
                  </span>

                  <h3 className="text-xl sm:text-2xl font-bold group-hover:text-[#59b5cd] transition">
                    {item.title}
                  </h3>

                  <p className="hidden lg:block text-gray-500 text-sm leading-relaxed max-w-md">
                    {item.description}
                  </p>

                  <span className="hidden sm:block text-[10px] tracking-[0.2em] text-gray-400 text-right">
                    {item.tag}
                  </span>

                </motion.div>
              ))}

            </div>

          </div>
        </section>

        {/* =========================================================
            PROOF
        ========================================================= */}
        <section className="py-28 sm:py-40 bg-white">

          <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">

            <div className="grid lg:grid-cols-[0.8fr_1.2fr] gap-16 items-end">

              <div>
                <p className="text-[#59b5cd] text-xs tracking-[0.3em] uppercase font-bold">
                  Evidence
                </p>

                <h2 className="mt-5 text-4xl sm:text-6xl font-black leading-tight">
                  Built for
                  <br />
                  <span className="text-gray-300">
                    measurable impact.
                  </span>
                </h2>
              </div>

              <div className="grid grid-cols-2 gap-px bg-gray-200 border border-gray-200">

                {[
                  ["500+", "Professionals trained"],
                  ["10+", "Programs delivered"],
                  ["10+", "Countries reached"],
                  ["95%", "Reported satisfaction"],
                ].map(([number, label]) => (
                  <motion.div
                    key={label}
                    whileHover={{ backgroundColor: "#07151c" }}
                    className="bg-white p-7 sm:p-10 group transition"
                  >
                    <div className="text-4xl sm:text-6xl font-black text-[#07151c] group-hover:text-[#59b5cd] transition">
                      {number}
                    </div>

                    <p className="mt-4 text-sm text-gray-500 group-hover:text-white/60">
                      {label}
                    </p>
                  </motion.div>
                ))}

              </div>

            </div>
          </div>
        </section>

        {/* =========================================================
            FINAL CTA
        ========================================================= */}
        <section className="relative overflow-hidden bg-[#07151c] text-white py-32 sm:py-48">

          {/* Decorative rings */}
          <div className="absolute inset-0 pointer-events-none">

            <div className="absolute w-[500px] h-[500px] border border-white/5 rounded-full -right-40 top-1/2 -translate-y-1/2" />

            <div className="absolute w-[350px] h-[350px] border border-[#59b5cd]/20 rounded-full -right-10 top-1/2 -translate-y-1/2" />

            <div className="absolute w-[200px] h-[200px] border border-[#ecb32c]/20 rounded-full right-16 top-1/2 -translate-y-1/2" />

            <div className="absolute w-96 h-96 bg-[#59b5cd]/10 blur-[120px] rounded-full left-[-150px] bottom-[-100px]" />

          </div>

          <div className="relative max-w-5xl mx-auto px-5 sm:px-8 text-center">

            <p className="text-[#59b5cd] text-xs font-bold tracking-[0.35em] uppercase">
              Start the conversation
            </p>

            <h2 className="mt-7 text-4xl sm:text-6xl lg:text-7xl font-black leading-[0.95] tracking-tight">
              Your team can do more.
              <br />
              <span className="text-white/30">
                Let's build the system.
              </span>
            </h2>

            <p className="mt-8 max-w-2xl mx-auto text-white/60 text-lg leading-relaxed">
              Tell us where your organization is today, where you want to go,
              and what is standing in the way. We'll help you design the
              capability system to get there.
            </p>

            <div className="mt-12">

              <a
                href="https://wa.me/2348084259372?text=Hello Zulfah Team, we’re interested in equipping our team with the technology skills needed to work smarter, improve productivity, and drive greater efficiency across our organisation. I’d like to learn more about your corporate training and how we can get started."
                target="_blank"
                rel="noopener noreferrer"
                className="group inline-flex items-center gap-4 px-9 py-5 rounded-full bg-white text-[#07151c] font-bold hover:bg-[#59b5cd] hover:text-white transition-all duration-300"
              >
                Start Your Transformation

                <span className="text-xl group-hover:translate-x-2 transition-transform">
                  →
                </span>
              </a>

            </div>

            <div className="mt-16 flex justify-center flex-wrap gap-8 text-xs text-white/40 uppercase tracking-widest">
              <span>Engineering</span>
              <span>•</span>
              <span>AI</span>
              <span>•</span>
              <span>Cybersecurity</span>
              <span>•</span>
              <span>Data</span>
              <span>•</span>
              <span>Product</span>
            </div>

          </div>
        </section>

      </main>
    </Layout>
  );
};

export default CorporateTraining;